import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import { LifecycleState, LifecycleTransition, LifecycleStateInfo, LifecycleTransitionResponse } from '../../models/file-vault.models';
import { LifecycleBadgeComponent } from '../lifecycle-badge/lifecycle-badge.component';

export interface TransitionEvent {
  entityType: string;
  entityId: string;
  fromState: LifecycleState;
  toState: LifecycleState;
  success: boolean;
  response?: LifecycleTransitionResponse;
  error?: string;
}

@Component({
  selector: 'fv-lifecycle-actions',
  standalone: true,
  template: `
    <div class="lifecycle-actions">
      <!-- Current State Badge -->
      <div class="current-state" *ngIf="showCurrentState && stateInfo">
        <span class="state-label">Current:</span>
        <fv-lifecycle-badge [state]="stateInfo.currentState" [showIcon]="true"></fv-lifecycle-badge>
      </div>

      <!-- Transition Buttons -->
      <div class="action-buttons" *ngIf="!isLoading">
        <ng-container *ngFor="let transition of allowedTransitions">
          <button kendoButton
                  [look]="getButtonLook(transition)"
                  [themeColor]="getButtonTheme(transition)"
                  (click)="openTransitionDialog(transition)"
                  [disabled]="isProcessing"
                  [title]="transition.description">
            {{ transition.transitionName }}
          </button>
        </ng-container>

        <span class="no-transitions" *ngIf="allowedTransitions.length === 0 && stateInfo">
          No transitions available
        </span>
      </div>

      <!-- Loading indicator -->
      <div class="loading" *ngIf="isLoading">
        Loading...
      </div>

      <!-- Transition Confirmation Dialog -->
      <kendo-dialog *ngIf="showDialog"
                    [title]="dialogTitle"
                    [width]="450"
                    (close)="closeDialog()">
        <div class="dialog-content">
          <div class="transition-info">
            <div class="state-change">
              <fv-lifecycle-badge [state]="stateInfo?.currentState" [showIcon]="true"></fv-lifecycle-badge>
              <span class="arrow">→</span>
              <fv-lifecycle-badge [state]="selectedTransition?.targetState" [showIcon]="true"></fv-lifecycle-badge>
            </div>
            <p class="description" *ngIf="selectedTransition?.description">
              {{ selectedTransition?.description }}
            </p>
          </div>

          <!-- Notes/Reason input -->
          <div class="input-section" *ngIf="selectedTransition?.requiresNotes || isObsolete || isDemotion">
            <label [for]="'notes-' + entityId">
              {{ isDemotion || isObsolete ? 'Reason (required)' : 'Notes' }}
              <span class="required" *ngIf="isDemotion || isObsolete">*</span>
            </label>
            <textarea kendoTextArea
                      [id]="'notes-' + entityId"
                      [(ngModel)]="transitionNotes"
                      [rows]="3"
                      [placeholder]="isDemotion || isObsolete ? 'Please provide a reason for this transition...' : 'Optional notes...'">
            </textarea>
          </div>

          <!-- Warning for demotions -->
          <div class="warning" *ngIf="isDemotion">
            <span class="warning-icon">⚠️</span>
            <span>Demoting will move this item to a lower lifecycle state.</span>
          </div>

          <!-- Warning for obsolete -->
          <div class="warning danger" *ngIf="isObsolete">
            <span class="warning-icon">⛔</span>
            <span>Marking as obsolete indicates this item is no longer valid for use.</span>
          </div>
        </div>

        <kendo-dialog-actions>
          <button kendoButton (click)="closeDialog()">Cancel</button>
          <button kendoButton
                  [themeColor]="isObsolete ? 'error' : 'primary'"
                  (click)="executeTransition()"
                  [disabled]="isProcessing || (requiresNotes && !transitionNotes?.trim())">
            {{ isProcessing ? 'Processing...' : 'Confirm' }}
          </button>
        </kendo-dialog-actions>
      </kendo-dialog>
    </div>
  `,
  styles: [`
    .lifecycle-actions {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
    }

    .current-state {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .state-label {
      font-size: 12px;
      color: var(--fv-text-secondary);
    }

    .action-buttons {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }

    .no-transitions {
      font-size: 12px;
      color: var(--fv-text-muted);
      font-style: italic;
    }

    .loading {
      font-size: 12px;
      color: var(--fv-text-secondary);
    }

    .dialog-content {
      padding: 16px 0;
    }

    .transition-info {
      margin-bottom: 20px;
    }

    .state-change {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 16px;
      padding: 16px;
      background: var(--fv-surface-alt);
      border-radius: 8px;
    }

    .arrow {
      font-size: 20px;
      color: var(--fv-text-secondary);
    }

    .description {
      text-align: center;
      color: var(--fv-text-secondary);
      margin-top: 12px;
      font-size: 14px;
    }

    .input-section {
      margin-top: 16px;
    }

    .input-section label {
      display: block;
      margin-bottom: 6px;
      font-weight: 500;
      font-size: 13px;
    }

    .required {
      color: var(--fv-status-error-fg);
    }

    .input-section textarea {
      width: 100%;
    }

    .warning {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      padding: 12px;
      background: var(--fv-status-warning-bg);
      border-radius: 4px;
      margin-top: 16px;
      font-size: 13px;
      color: var(--fv-status-warning-fg);
    }

    .warning.danger {
      background: var(--fv-status-error-bg);
      color: var(--fv-status-error-fg);
    }

    .warning-icon {
      flex-shrink: 0;
    }
  `],
  imports: [CommonModule, FormsModule, ButtonsModule, DialogModule, InputsModule, LifecycleBadgeComponent]
})
export class LifecycleActionsComponent implements OnInit, OnChanges {
  private api = inject(FileVaultApiService);

  /** Entity type (Part, PartVersion, Document, DocumentVersion) */
  @Input() entityType!: string;

  /** Entity ID */
  @Input() entityId!: string;

  /** Show current state badge */
  @Input() showCurrentState = true;

  /** Emitted when a transition is completed */
  @Output() transitionCompleted = new EventEmitter<TransitionEvent>();

  /** Emitted when a transition fails */
  @Output() transitionFailed = new EventEmitter<TransitionEvent>();

  /** Emitted when state is refreshed */
  @Output() stateChanged = new EventEmitter<LifecycleStateInfo>();

  stateInfo?: LifecycleStateInfo;
  allowedTransitions: LifecycleTransition[] = [];
  isLoading = false;
  isProcessing = false;

  // Dialog state
  showDialog = false;
  selectedTransition?: LifecycleTransition;
  transitionNotes = '';

  get dialogTitle(): string {
    return this.selectedTransition?.transitionName ?? 'Confirm Transition';
  }

  get isDemotion(): boolean {
    if (!this.stateInfo || !this.selectedTransition) return false;
    return this.selectedTransition.targetState < this.stateInfo.currentState;
  }

  get isObsolete(): boolean {
    return this.selectedTransition?.targetState === LifecycleState.Obsolete;
  }

  get requiresNotes(): boolean {
    return this.selectedTransition?.requiresNotes || this.isDemotion || this.isObsolete;
  }

  ngOnInit(): void {
    this.loadState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['entityType'] || changes['entityId']) && !changes['entityType']?.firstChange) {
      this.loadState();
    }
  }

  async loadState(): Promise<void> {
    if (!this.entityType || !this.entityId) return;

    this.isLoading = true;
    try {
      const [stateInfo, transitions] = await Promise.all([
        this.api.getLifecycleState(this.entityType, this.entityId).toPromise(),
        this.api.getAllowedTransitions(this.entityType, this.entityId).toPromise()
      ]);

      this.stateInfo = stateInfo;
      this.allowedTransitions = transitions ?? [];

      if (stateInfo) {
        this.stateChanged.emit(stateInfo);
      }
    } catch (error) {
      console.error('Failed to load lifecycle state', error);
    } finally {
      this.isLoading = false;
    }
  }

  openTransitionDialog(transition: LifecycleTransition): void {
    this.selectedTransition = transition;
    this.transitionNotes = '';
    this.showDialog = true;
  }

  closeDialog(): void {
    this.showDialog = false;
    this.selectedTransition = undefined;
    this.transitionNotes = '';
  }

  async executeTransition(): Promise<void> {
    if (!this.selectedTransition || !this.stateInfo) return;

    this.isProcessing = true;
    const fromState = this.stateInfo.currentState;
    const toState = this.selectedTransition.targetState;

    try {
      let response: LifecycleTransitionResponse | undefined;

      if (this.isObsolete) {
        response = await this.api.obsoleteLifecycle(
          this.entityType,
          this.entityId,
          this.transitionNotes
        ).toPromise();
      } else if (this.isDemotion) {
        response = await this.api.demoteLifecycle(
          this.entityType,
          this.entityId,
          toState,
          this.transitionNotes
        ).toPromise();
      } else {
        response = await this.api.promoteLifecycle(
          this.entityType,
          this.entityId,
          toState,
          this.transitionNotes || undefined
        ).toPromise();
      }

      if (response?.success) {
        this.transitionCompleted.emit({
          entityType: this.entityType,
          entityId: this.entityId,
          fromState,
          toState,
          success: true,
          response
        });

        // Refresh state
        await this.loadState();
      } else {
        throw new Error('Transition failed');
      }

      this.closeDialog();
    } catch (error: any) {
      this.transitionFailed.emit({
        entityType: this.entityType,
        entityId: this.entityId,
        fromState,
        toState,
        success: false,
        error: error.message || 'Transition failed'
      });
    } finally {
      this.isProcessing = false;
    }
  }

  getButtonLook(transition: LifecycleTransition): 'flat' | 'outline' {
    // Demotions and obsolete use outline style
    if (this.stateInfo && transition.targetState < this.stateInfo.currentState) {
      return 'outline';
    }
    if (transition.targetState === LifecycleState.Obsolete) {
      return 'outline';
    }
    return 'flat';
  }

  getButtonTheme(transition: LifecycleTransition): 'primary' | 'warning' | 'error' | 'base' {
    if (transition.targetState === LifecycleState.Obsolete) {
      return 'error';
    }
    if (transition.targetState === LifecycleState.Released) {
      return 'primary';
    }
    if (this.stateInfo && transition.targetState < this.stateInfo.currentState) {
      return 'warning';
    }
    return 'base';
  }

  /** Refresh the state and transitions */
  refresh(): void {
    this.loadState();
  }
}
